import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ui, cn } from '../lib/ui';
import { useOrg } from '../lib/org';
import {
  PAGE_ORDER,
  contrastFeedback,
  editablePages,
  isHex,
  logoFileError,
  needsPolling,
  pagesPayload,
  previewTokens,
  scanStatus,
} from '../lib/sites';
import * as sitesApi from '../api/sites';
import SiteTemplate from '../components/sites/SiteTemplate';
import SitePattern from '../components/sites/SitePattern';
import SiteUploads from '../components/sites/SiteUploads';
import SiteDomains from '../components/sites/SiteDomains';
import HostSetup from '../components/HostSetup';

/**
 * Website builder (docs/Decisions.md D43), /org/site. For platform admins, org
 * admins and members of the org's delegated editor groups; the server decides
 * and this page shows what it said. Colours shown here (palette swatches, the
 * preview) are the organization's content, applied from data at runtime.
 */

const COLOR_ROLES = [
  { id: 'canvas', label: 'Page background' },
  { id: 'surface', label: 'Cards' },
  { id: 'ink', label: 'Text' },
  { id: 'accent', label: 'Buttons and links' },
  { id: 'band', label: 'Header and footer' },
];

const EMPTY_BLOCK = { heading: '', body: '', linkLabel: '', linkUrl: '' };

function draftFrom(site) {
  return {
    template: site.template,
    pattern: site.pattern,
    preset: site.palette?.preset || 'harbor',
    colors: { ...(site.palette?.colors || {}) },
    siteTitle: site.siteTitle || '',
    tagline: site.tagline || '',
    pages: editablePages(site.pages),
  };
}

function Swatch({ color }) {
  // An organization colour, rendered from data.
  return (
    <span
      aria-hidden="true"
      className="inline-block size-5 rounded-full border border-z-line"
      style={{ backgroundColor: color }}
    />
  );
}

function Section({ id, title, children, actions }) {
  return (
    <section className={ui.card} aria-labelledby={id}>
      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <h2 id={id} className={cn(ui.h2, 'mb-0!')}>
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

export default function SiteBuilder() {
  const { activeOrg } = useOrg();
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [previewPage, setPreviewPage] = useState('home');
  const [groups, setGroups] = useState(null);
  const [editorIds, setEditorIds] = useState([]);
  const [logoFile, setLogoFile] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await sitesApi.getSiteConfig();
      setConfig(data);
      setLoadError(null);
      return data;
    } catch (err) {
      setLoadError(err);
      return null;
    }
  }, []);

  useEffect(() => {
    setConfig(null);
    setDraft(null);
    load().then(data => {
      if (data) {
        setDraft(draftFrom(data.site));
        setEditorIds(data.site.editorGroupIds || []);
        if (data.access.canDelegate) {
          sitesApi
            .listSiteGroups()
            .then(res => setGroups(res.groups || []))
            .catch(() => setGroups([]));
        }
      }
    });
  }, [load, activeOrg?.orgId]);

  // Poll while a logo scan is running.
  useEffect(() => {
    if (!config || !needsPolling([], config.site.logoPending)) return undefined;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [config, load]);

  const catalog = config?.catalog;
  const presetTokens = useMemo(() => {
    const preset = catalog?.palettes?.find(p => p.id === draft?.preset);
    return preset ? preset.tokens : null;
  }, [catalog, draft?.preset]);

  if (loadError) {
    const forbidden = loadError.statusCode === 403;
    return (
      <div className={cn(ui.container, ui.page)} data-zone="console">
        <h1 className={ui.h1}>Website</h1>
        <p className={forbidden ? ui.alertInfo : ui.alertError} role="alert">
          {loadError.message}
        </p>
      </div>
    );
  }

  if (!config || !draft) {
    return (
      <div className={cn(ui.container, ui.page)} data-zone="console">
        <h1 className={ui.h1}>Website</h1>
        <p className={ui.muted} role="status">
          Loading the website settings.
        </p>
      </div>
    );
  }

  const { site, access, org, hosting } = config;
  const customColorsValid = COLOR_ROLES.every(r => isHex(draft.colors[r.id]));
  const tokens =
    draft.preset === 'custom'
      ? previewTokens(draft.colors) || site.palette?.tokens
      : presetTokens || site.palette?.tokens;
  const previewSite = {
    template: draft.template,
    pattern: draft.pattern,
    palette: { tokens },
    siteTitle: draft.siteTitle,
    tagline: draft.tagline,
    pages: draft.pages,
  };

  const update = patch => setDraft(d => ({ ...d, ...patch }));
  const updatePage = (pageId, patch) =>
    setDraft(d => ({ ...d, pages: { ...d.pages, [pageId]: { ...d.pages[pageId], ...patch } } }));
  const updateBlock = (pageId, index, patch) =>
    setDraft(d => {
      const blocks = d.pages[pageId].blocks.map((b, i) => (i === index ? { ...b, ...patch } : b));
      return { ...d, pages: { ...d.pages, [pageId]: { ...d.pages[pageId], blocks } } };
    });

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      const palette =
        draft.preset === 'custom'
          ? { preset: 'custom', colors: draft.colors }
          : { preset: draft.preset };
      const res = await sitesApi.updateSiteConfig({
        template: draft.template,
        pattern: draft.pattern,
        palette,
        siteTitle: draft.siteTitle,
        tagline: draft.tagline,
        pages: pagesPayload(draft.pages),
      });
      setConfig(c => ({ ...c, site: res.site }));
      setDraft(draftFrom(res.site));
      setMessage({
        tone: 'success',
        text: res.adjustments?.length ? `Saved. ${res.adjustments.join(' ')}` : 'Saved.',
      });
    } catch (err) {
      setMessage({ tone: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  }

  async function togglePublished() {
    setMessage(null);
    try {
      const res = await sitesApi.setSitePublished(!site.published);
      setConfig(c => ({ ...c, site: res.site }));
      setMessage({
        tone: 'success',
        text: res.site.published ? 'Your site is published.' : 'Your site is unpublished.',
      });
    } catch (err) {
      setMessage({ tone: 'error', text: err.message });
    }
  }

  async function uploadLogo(e) {
    e.preventDefault();
    const problem = logoFileError(logoFile, catalog.logo);
    if (problem) {
      setMessage({ tone: 'error', text: problem });
      return;
    }
    setMessage(null);
    try {
      const res = await sitesApi.requestLogoUpload(logoFile);
      await sitesApi.putToPresignedUrl(res.uploadUrl, logoFile, logoFile.type);
      await sitesApi.confirmLogo(res.logoId);
      setLogoFile(null);
      await load();
      setMessage({
        tone: 'success',
        text: 'Logo uploaded. It appears on the site once the scan passes.',
      });
    } catch (err) {
      setMessage({ tone: 'error', text: err.message });
    }
  }

  async function saveEditors() {
    setMessage(null);
    try {
      const res = await sitesApi.setSiteEditors(editorIds);
      setEditorIds(res.editorGroupIds);
      setMessage({ tone: 'success', text: 'Editor groups saved.' });
    } catch (err) {
      setMessage({ tone: 'error', text: err.message });
    }
  }

  const feedback = contrastFeedback(draft.colors);
  const pending = site.logoPending;

  return (
    <div className={cn(ui.container, ui.page, 'flex flex-col gap-6')} data-zone="console">
      <div className={cn(ui.row, ui.spread)}>
        <div>
          <p className={ui.eyebrow}>{org.name}</p>
          <h1 className={cn(ui.h1, 'mb-0!')}>Website</h1>
        </div>
        <div className={ui.row}>
          <span className={site.published ? ui.pillSuccess : ui.pillNeutral}>
            {site.published ? 'Published' : 'Draft'}
          </span>
          {site.published ? (
            <Link
              className={cn(ui.btnSecondary, ui.btnSm)}
              to={config.publicPath}
              target="_blank"
              rel="noopener noreferrer"
            >
              View site
            </Link>
          ) : null}
          <button
            type="button"
            className={cn(site.published ? ui.btnSecondary : ui.btnPrimary, ui.btnSm)}
            onClick={togglePublished}
          >
            {site.published ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      </div>

      {message ? (
        <p
          className={message.tone === 'error' ? ui.alertError : ui.alertSuccess}
          role={message.tone === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <div className="flex flex-col gap-6">
          <Section id="site-template" title="Template">
            <fieldset>
              <legend className="sr-only">Template</legend>
              <div className={cn(ui.grid, ui.gridMd3)}>
                {catalog.templates.map(t => (
                  <label
                    key={t.id}
                    className={cn(
                      'flex cursor-pointer flex-col gap-2 rounded-z-card border p-3',
                      draft.template === t.id
                        ? 'border-dcon-brand bg-dcon-brand-soft'
                        : 'border-z-line'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="template"
                        className={ui.checkbox}
                        checked={draft.template === t.id}
                        onChange={() => update({ template: t.id })}
                      />
                      <span className="font-display text-label">{t.label}</span>
                    </span>
                    <TemplateThumb templateId={t.id} />
                    <span className="text-caption text-dcon-ink-muted">{t.description}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </Section>

          <Section id="site-palette" title="Colours">
            <fieldset>
              <legend className="sr-only">Palette</legend>
              <div className="flex flex-wrap gap-2">
                {catalog.palettes.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={draft.preset === p.id}
                    className={cn(
                      ui.btnSecondary,
                      ui.btnSm,
                      draft.preset === p.id ? 'bg-dcon-brand-soft' : ''
                    )}
                    onClick={() => update({ preset: p.id, colors: { ...p.colors } })}
                  >
                    <span className="flex gap-1">
                      {['band', 'accent', 'canvas'].map(role => (
                        <Swatch key={role} color={p.colors[role]} />
                      ))}
                    </span>
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  aria-pressed={draft.preset === 'custom'}
                  className={cn(
                    ui.btnSecondary,
                    ui.btnSm,
                    draft.preset === 'custom' ? 'bg-dcon-brand-soft' : ''
                  )}
                  onClick={() => update({ preset: 'custom' })}
                >
                  Custom
                </button>
              </div>
            </fieldset>
            {draft.preset === 'custom' ? (
              <div className="mt-4 flex flex-col gap-3">
                {COLOR_ROLES.map(role => (
                  <div key={role.id} className="grid grid-cols-3 items-center gap-3">
                    <label
                      className="text-body-s font-semibold text-dcon-ink-body"
                      htmlFor={`color-${role.id}`}
                    >
                      {role.label}
                    </label>
                    <input
                      id={`color-${role.id}`}
                      className={ui.input}
                      value={draft.colors[role.id] || ''}
                      placeholder="#RRGGBB"
                      onChange={e =>
                        update({ colors: { ...draft.colors, [role.id]: e.target.value.trim() } })
                      }
                    />
                    {isHex(draft.colors[role.id]) ? (
                      <input
                        type="color"
                        aria-label={`${role.label} colour picker`}
                        className="h-ctl-sm w-full cursor-pointer rounded-dcon-md border border-dcon-line-strong bg-z-surface"
                        value={draft.colors[role.id].toLowerCase()}
                        onChange={e =>
                          update({
                            colors: { ...draft.colors, [role.id]: e.target.value.toUpperCase() },
                          })
                        }
                      />
                    ) : (
                      <span className={ui.hint}>Enter a #RRGGBB colour</span>
                    )}
                  </div>
                ))}
                <ul className="flex flex-col gap-1" aria-label="Contrast check">
                  {feedback.map(f => (
                    <li key={f.label} className="flex items-center gap-2 text-body-s">
                      <span className={f.pass ? ui.pillSuccess : ui.pillWarning}>
                        {f.pass ? 'Passes AA' : 'Below AA'}
                      </span>
                      <span>
                        {f.label}: {f.ratio === null ? 'enter a #RRGGBB colour' : `${f.ratio}:1`}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className={ui.hint}>
                  Text needs 4.5:1. When saving, a text or link colour below that is darkened or
                  lightened until it passes, and button and header labels are chosen for you.
                </p>
              </div>
            ) : null}
          </Section>

          <Section id="site-pattern" title="Background pattern">
            <fieldset>
              <legend className="sr-only">Background pattern</legend>
              <div className="flex flex-wrap gap-3">
                {catalog.patterns.map(p => (
                  <label
                    key={p.id}
                    className={cn(
                      'flex cursor-pointer flex-col items-center gap-2 rounded-z-card border p-2',
                      draft.pattern === p.id
                        ? 'border-dcon-brand bg-dcon-brand-soft'
                        : 'border-z-line'
                    )}
                  >
                    <span className="relative block h-12 w-20 overflow-hidden rounded-dcon-sm bg-z-sunken">
                      <SitePattern pattern={p.id} />
                    </span>
                    <span className="flex items-center gap-2 text-body-s">
                      <input
                        type="radio"
                        name="pattern"
                        className={ui.checkbox}
                        checked={draft.pattern === p.id}
                        onChange={() => update({ pattern: p.id })}
                      />
                      {p.label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </Section>

          <Section id="site-logo" title="Logo">
            <div className="flex flex-col gap-3">
              <p className="text-body-s">
                Current logo:{' '}
                {site.logo ? (
                  <span className={ui.pillSuccess}>In use</span>
                ) : (
                  <span className={ui.pillNeutral}>None</span>
                )}
                {pending ? (
                  <span className="ml-2">
                    New logo:{' '}
                    <span className={ui.pillInfo}>{scanStatus(pending.status).label}</span>
                  </span>
                ) : null}
              </p>
              {pending?.status === 'rejected' && pending.reasons?.length ? (
                <p className={ui.alertError}>{pending.reasons.join(' ')}</p>
              ) : null}
              {config.logoUrl ? (
                <img
                  src={config.logoUrl}
                  alt={`${org.name} logo`}
                  className="size-20 rounded-dcon-md border border-z-line object-contain p-1"
                />
              ) : null}
              {hosting.configured ? (
                <form className={ui.row} onSubmit={uploadLogo}>
                  <label className={cn(ui.field, 'mb-0!')}>
                    <span className={ui.label}>PNG, JPEG or WebP, up to 1 MB</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className={ui.input}
                      onChange={e => setLogoFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  <button type="submit" className={cn(ui.btnSecondary, ui.btnSm)}>
                    Upload logo
                  </button>
                  {site.logo ? (
                    <button
                      type="button"
                      className={cn(ui.btnGhost, ui.btnSm)}
                      onClick={async () => {
                        await sitesApi.removeLogo();
                        await load();
                      }}
                    >
                      Remove logo
                    </button>
                  ) : null}
                </form>
              ) : (
                <p className={ui.hint}>Logo uploads are not set up in this environment yet.</p>
              )}
            </div>
          </Section>

          <Section id="site-content" title="Pages and text">
            <label className={ui.field}>
              <span className={ui.label}>Site title</span>
              <input
                className={ui.input}
                maxLength={catalog.limits.siteTitle}
                value={draft.siteTitle}
                onChange={e => update({ siteTitle: e.target.value })}
              />
            </label>
            <label className={ui.field}>
              <span className={ui.label}>Tagline</span>
              <input
                className={ui.input}
                maxLength={catalog.limits.tagline}
                value={draft.tagline}
                onChange={e => update({ tagline: e.target.value })}
              />
            </label>
            <div className="flex flex-col gap-4">
              {PAGE_ORDER.map(pageId => {
                const page = draft.pages[pageId];
                return (
                  <details
                    key={pageId}
                    className="rounded-z-card border border-z-line p-4"
                    open={pageId === 'home'}
                  >
                    <summary className="cursor-pointer font-display text-label">
                      {page.title || pageId} {page.enabled ? '' : '(hidden)'}
                    </summary>
                    <div className="mt-3 flex flex-col gap-3">
                      {pageId !== 'home' ? (
                        <label className="flex items-center gap-2 text-body-s">
                          <input
                            type="checkbox"
                            className={ui.checkbox}
                            checked={page.enabled}
                            onChange={e => updatePage(pageId, { enabled: e.target.checked })}
                          />
                          Show this page
                        </label>
                      ) : null}
                      <label className={cn(ui.field, 'mb-0!')}>
                        <span className={ui.label}>Page title</span>
                        <input
                          className={ui.input}
                          maxLength={catalog.limits.pageTitle}
                          value={page.title}
                          onChange={e => updatePage(pageId, { title: e.target.value })}
                        />
                      </label>
                      {page.blocks.map((block, i) => (
                        <fieldset key={i} className="rounded-dcon-md border border-z-line p-3">
                          <legend className="px-1 text-caption text-dcon-ink-muted">
                            Block {i + 1}
                          </legend>
                          <label className={ui.field}>
                            <span className={ui.label}>Heading</span>
                            <input
                              className={ui.input}
                              maxLength={catalog.limits.blockHeading}
                              value={block.heading}
                              onChange={e => updateBlock(pageId, i, { heading: e.target.value })}
                            />
                          </label>
                          <label className={ui.field}>
                            <span className={ui.label}>Text</span>
                            <textarea
                              className={ui.input}
                              rows={4}
                              maxLength={catalog.limits.blockBody}
                              value={block.body}
                              onChange={e => updateBlock(pageId, i, { body: e.target.value })}
                            />
                            <span className={ui.hint}>
                              Plain text. Leave a blank line between paragraphs.
                            </span>
                          </label>
                          <div className="grid gap-3 md:grid-cols-2">
                            <label className={cn(ui.field, 'mb-0!')}>
                              <span className={ui.label}>Button label (optional)</span>
                              <input
                                className={ui.input}
                                maxLength={catalog.limits.linkLabel}
                                value={block.linkLabel}
                                onChange={e =>
                                  updateBlock(pageId, i, { linkLabel: e.target.value })
                                }
                              />
                            </label>
                            <label className={cn(ui.field, 'mb-0!')}>
                              <span className={ui.label}>Button link (https:// or mailto:)</span>
                              <input
                                className={ui.input}
                                value={block.linkUrl}
                                onChange={e => updateBlock(pageId, i, { linkUrl: e.target.value })}
                              />
                            </label>
                          </div>
                          <button
                            type="button"
                            className={cn(ui.btnGhost, ui.btnSm, 'mt-3')}
                            onClick={() =>
                              updatePage(pageId, { blocks: page.blocks.filter((_, j) => j !== i) })
                            }
                          >
                            Remove block
                          </button>
                        </fieldset>
                      ))}
                      {page.blocks.length < catalog.limits.blocksPerPage ? (
                        <button
                          type="button"
                          className={cn(ui.btnSecondary, ui.btnSm, 'self-start')}
                          onClick={() =>
                            updatePage(pageId, { blocks: [...page.blocks, { ...EMPTY_BLOCK }] })
                          }
                        >
                          Add a block
                        </button>
                      ) : null}
                      {pageId === 'events' ? (
                        <p className={ui.hint}>
                          Your upcoming public events are listed below these blocks.
                        </p>
                      ) : null}
                    </div>
                  </details>
                );
              })}
            </div>
          </Section>

          <div
            className={cn(
              ui.row,
              'sticky bottom-0 rounded-z-card border border-z-line bg-z-surface p-3 shadow-dcon-md'
            )}
          >
            <button
              type="button"
              className={ui.btnPrimary}
              onClick={save}
              disabled={saving || (draft.preset === 'custom' && !customColorsValid)}
            >
              {saving ? 'Saving' : 'Save changes'}
            </button>
            {draft.preset === 'custom' && !customColorsValid ? (
              <span className={ui.hint}>Enter all five colours as #RRGGBB to save.</span>
            ) : null}
          </div>

          {access.canDelegate ? (
            <Section id="site-editors" title="Who can edit the website">
              <p className="mb-3 text-body-s text-dcon-ink-body">
                Org admins can always edit. Choose groups whose members may edit too; every change
                is recorded in the audit log.
              </p>
              {groups === null ? <p className={ui.muted}>Loading groups.</p> : null}
              {groups && !groups.length ? (
                <p className={ui.muted}>
                  This organization has no groups yet. Create one on the Groups page.
                </p>
              ) : null}
              {groups && groups.length ? (
                <fieldset className="flex flex-col gap-2">
                  <legend className="sr-only">Editor groups</legend>
                  {groups.map(g => (
                    <label key={g.groupId} className="flex items-center gap-2 text-body-s">
                      <input
                        type="checkbox"
                        className={ui.checkbox}
                        checked={editorIds.includes(g.groupId)}
                        onChange={e =>
                          setEditorIds(ids =>
                            e.target.checked
                              ? [...ids, g.groupId]
                              : ids.filter(id => id !== g.groupId)
                          )
                        }
                      />
                      {g.name}
                      <span className={ui.subtle}>({g.memberCount} members)</span>
                    </label>
                  ))}
                  <button
                    type="button"
                    className={cn(ui.btnSecondary, ui.btnSm, 'mt-2 self-start')}
                    onClick={saveEditors}
                  >
                    Save editor groups
                  </button>
                </fieldset>
              ) : null}
            </Section>
          ) : null}

          {access.canDelegate ? (
            <Section id="site-address" title="Your website address">
              {activeOrg?.websiteDomain ? (
                <p className="text-body-s">
                  Your site is live at{' '}
                  <a href={`https://${activeOrg.websiteDomain}`} rel="noopener noreferrer">
                    {activeOrg.websiteDomain}
                  </a>
                  {activeOrg.customDomain ? (
                    <>
                      , and members sign in at <strong>{activeOrg.customDomain}</strong>
                    </>
                  ) : null}
                  . Rename either address in{' '}
                  <Link to="/org/settings">Organization settings</Link>.
                </p>
              ) : (
                <>
                  <p className={cn(ui.hint, 'mb-3')}>
                    Show this site at your own domain: the website address (www by default) shows
                    it, and the portal address (portal by default) is where members sign in. Each
                    gets its own certificate. Until then the site is at {config.publicPath}.
                  </p>
                  <HostSetup />
                </>
              )}
            </Section>
          ) : null}

          <SiteUploads
            canUpload={access.canUpload}
            hostingConfigured={hosting.configured}
            limits={catalog.upload}
          />

          <SiteDomains
            canUpload={access.canUpload}
            canManage={access.canDelegate}
            hostingConfigured={hosting.configured}
          />
        </div>

        <div className="lg:sticky lg:top-4">
          <div className={cn(ui.row, ui.spread, 'mb-2')}>
            <h2 className={cn(ui.h3, 'mb-0!')}>Preview</h2>
            <span className={ui.hint}>Unsaved changes show here first.</span>
          </div>
          <div className="max-h-screen overflow-auto rounded-z-card border border-z-line shadow-dcon-md">
            <SiteTemplate
              site={previewSite}
              orgName={org.name}
              slug={org.slug}
              logoUrl={config.logoUrl}
              pageId={draft.pages[previewPage]?.enabled !== false ? previewPage : 'home'}
              events={[]}
              eventsState="ready"
              onNavigate={setPreviewPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/** A schematic of each template's layout, in neutral design-system fills. */
function TemplateThumb({ templateId }) {
  const bar = 'rounded-dcon-sm bg-dcon-deep';
  const card = 'rounded-dcon-sm border border-z-line bg-z-surface';
  return (
    <span aria-hidden="true" className="flex h-20 flex-col gap-1 rounded-dcon-md bg-z-sunken p-2">
      {templateId === 'modern' ? (
        <>
          <span className={cn(bar, 'h-2')} />
          <span className="h-3 w-2/3 rounded-dcon-sm bg-dcon-ink-muted" />
          <span className="grid flex-1 grid-cols-2 gap-1">
            <span className={card} />
            <span className={card} />
          </span>
        </>
      ) : templateId === 'community' ? (
        <>
          <span className={cn(bar, 'h-5')} />
          <span className="grid flex-1 grid-cols-3 gap-1">
            <span className={card} />
            <span className={card} />
            <span className={card} />
          </span>
        </>
      ) : (
        <>
          <span className={cn(bar, 'flex h-6 items-center justify-center')}>
            <span className="h-2 w-1/3 rounded-dcon-sm bg-dcon-on-deep" />
          </span>
          <span className={cn(card, 'mx-auto w-2/3 flex-1')} />
        </>
      )}
    </span>
  );
}
